import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { getAccessToken } from '../services/authService';

function formatUsd(usd) {
    if (!usd || usd === 0) return '$0.00';
    for (let d = 2; d <= 8; d++) {
        if (parseFloat(usd.toFixed(d)) !== 0) {
            return `$${usd.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
        }
    }
    return '<$0.000000001';
}

function cleanPoolName(name) {
    if (name === 'You Bee General Hive' || name === 'You Bee General Pool') return 'You Bee General Hive';
    return name.replace(/^(Industry|State|City|Company): /, '');
}

function poolTypeLabel(name) {
    if (name === 'You Bee General Hive') return 'National';
    if (name.startsWith('Industry: ')) return 'Industry';
    if (name.startsWith('State: ')) return 'State';
    if (name.startsWith('City: ')) return 'City';
    return 'Pool';
}

function MetricCard({ value, label, sub }) {
    return (
        <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px',
            padding: '28px 24px',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        }}>
            <div style={{
                fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
                fontWeight: 800,
                color: '#ffc107',
                lineHeight: 1.1,
            }}>{value}</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</div>
            {sub && <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>{sub}</div>}
        </div>
    );
}

function Stats() {
    const [stats, setStats] = useState(null);
    const [solPrice, setSolPrice] = useState(null);
    const [recentTxs, setRecentTxs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = async () => {
        try {
            const [statsRes, priceRes, txRes] = await Promise.all([
                api.get('/stats').catch(() => ({ data: null })),
                api.get('/sol-price').catch(() => ({ data: { price: null } })),
                api.get('/transactions?limit=100').catch(() => ({ data: [] })),
            ]);
            setStats(statsRes.data);
            setSolPrice(priceRes.data.price);
            const raw = (txRes.data || []).filter(tx =>
                tx.note === 'give_back' ||
                tx.recipient_address?.startsWith('Distribution:') ||
                tx.recipient_address?.startsWith('Pool:') ||
                tx.recipient_address?.startsWith('Give Back:')
            );
            // Aggregate Distribution: records by pool + hour so each run shows one entry
            const buckets = {};
            const nonDist = [];
            for (const tx of raw) {
                if (tx.recipient_address?.startsWith('Distribution:')) {
                    const hour = new Date(tx.timestamp).toISOString().slice(0, 13);
                    const key = `${tx.recipient_address}|${hour}`;
                    if (!buckets[key]) buckets[key] = { ...tx, usd_value: 0, amount: 0 };
                    buckets[key].usd_value += parseFloat(tx.usd_value ?? tx.amount ?? 0);
                    buckets[key].amount    += parseFloat(tx.amount ?? 0);
                } else {
                    nonDist.push(tx);
                }
            }
            const meaningful = [...Object.values(buckets), ...nonDist]
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .slice(0, 20);
            setRecentTxs(meaningful);
        } catch (err) {
            console.error('Stats fetch error:', err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        const interval = setInterval(fetchAll, 30000);
        return () => clearInterval(interval);
    }, []);

    const maxPoolAmount = stats?.top_pools?.length > 0
        ? Math.max(...stats.top_pools.map(p => p.current_amount))
        : 1;

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Orbs */}
            <div style={{ position: 'fixed', top: '-15%', right: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(255,193,7,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />
            <div style={{ position: 'fixed', bottom: '-20%', left: '-10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(255,193,7,0.07) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />

            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '60px 20px 40px', position: 'relative', zIndex: 10 }}>

                {/* Hero */}
                <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                    <h1 style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        fontWeight: 800,
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.7) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        margin: '0 0 12px',
                    }}>
                        You Bee by the Numbers
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem', margin: 0 }}>
                        Real-time community impact
                    </p>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '60px 0' }}>Loading stats…</div>
                ) : !stats ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '60px 0' }}>
                        Stats unavailable — please try again shortly.
                    </div>
                ) : (
                    <>
                        {/* Metric Cards */}
                        <style>{`
                            .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 40px; }
                            .pool-bar-fill { transition: width 0.8s ease; }
                            @media (max-width: 600px) { .stats-grid { grid-template-columns: 1fr 1fr; } }
                        `}</style>
                        <div className="stats-grid">
                            <MetricCard
                                value={stats?.total_users?.toLocaleString() ?? '—'}
                                label="Community Members"
                                sub={<>{stats?.wallet_users?.toLocaleString() ?? '—'} with active wallets<br />✔ {stats?.kyc_verified_users?.toLocaleString() ?? '0'} KYC verified</>}
                            />
                            <MetricCard
                                value={`$${(stats?.total_distributed_sol ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                label="Total Distributed"
                                sub="All-time UBI delivered"
                            />
                            <MetricCard
                                value={stats?.total_givebacks_count?.toLocaleString() ?? '—'}
                                label="Community Give-Backs"
                                sub={formatUsd(stats?.total_givebacks_sol ?? 0) + ' returned'}
                            />
                            <MetricCard
                                value={stats?.active_pools?.toLocaleString() ?? '—'}
                                label="Active Hives"
                                sub="Funding communities"
                            />
                        </div>

                        {/* Top 5 Pools */}
                        {stats?.top_pools?.length > 0 && (
                            <div style={{
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '20px',
                                padding: '28px',
                                backdropFilter: 'blur(20px)',
                                marginBottom: '32px',
                            }}>
                                <h3 style={{ margin: '0 0 24px', color: '#ffc107', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 700 }}>
                                    Top 5 Hives by Size
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {stats.top_pools.map((p, i) => {
                                        const barPct = maxPoolAmount > 0 ? (p.current_amount / maxPoolAmount) * 100 : 0;
                                        return (
                                            <div key={i}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px',
                                                            borderRadius: '8px', background: 'rgba(255,193,7,0.12)',
                                                            border: '1px solid rgba(255,193,7,0.25)', color: '#ffc107',
                                                        }}>
                                                            {poolTypeLabel(p.name)}
                                                        </span>
                                                        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{cleanPoolName(p.name)}</span>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '0.95rem' }}>
                                                            {formatUsd(p.current_amount)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '6px', height: '8px', overflow: 'hidden', marginBottom: '4px' }}>
                                                    <div className="pool-bar-fill" style={{
                                                        width: `${barPct}%`,
                                                        height: '100%',
                                                        background: 'linear-gradient(90deg, #ffc107, #ff9800)',
                                                        borderRadius: '6px',
                                                    }} />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)' }}>
                                                        {p.recipient_count.toLocaleString()} {p.recipient_count === 1 ? 'recipient' : 'recipients'} currently eligible
                                                    </span>
                                                    {p.daily_per_person_sol > 0 && (
                                                        <span style={{ fontSize: '0.75rem', color: 'rgba(34,197,94,0.7)', fontWeight: 600 }}>
                                                            ~{formatUsd(p.daily_per_person_sol)}/day per person
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Recent Activity */}
                        {recentTxs.length > 0 && (
                            <div style={{
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '20px',
                                padding: '28px',
                                backdropFilter: 'blur(20px)',
                                marginBottom: '40px',
                            }}>
                                <h3 style={{ margin: '0 0 20px', color: '#ffc107', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 700 }}>
                                    Recent Activity
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                                    {recentTxs.map((tx, i) => {
                                        const label = tx.note === 'give_back' || tx.recipient_address?.startsWith('Give Back:')
                                            ? 'Community Give Back'
                                            : tx.recipient_address?.startsWith('Distribution:')
                                            ? (() => { const n = cleanPoolName(tx.recipient_address.replace(/^Distribution: /, '')); return n === 'You Bee General Hive' ? n + ' distributed' : n + ' hive distributed'; })()
                                            : tx.recipient_address?.startsWith('Pool:')
                                            ? 'Contribution to ' + cleanPoolName(tx.recipient_address.replace(/^Pool: /, ''))
                                            : tx.note
                                            ? `A You Bee user received UBI from ${cleanPoolName(tx.note)}`
                                            : 'Transaction';
                                        const usd = Math.abs(parseFloat(tx.usd_value ?? tx.amount ?? 0));
                                        return (
                                            <div key={tx.id} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '10px 0',
                                                borderBottom: i < recentTxs.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                                gap: '12px',
                                                flexWrap: 'wrap',
                                            }}>
                                                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', flex: 1, minWidth: '160px' }}>{label}</span>
                                                <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                                    {formatUsd(usd)}
                                                </span>
                                                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                    {new Date(tx.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* CTA — only show to logged-out visitors */}
                {!getAccessToken() && <div style={{
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,152,0,0.06) 100%)',
                    border: '1px solid rgba(255,193,7,0.2)',
                    borderRadius: '24px',
                    padding: '40px 24px',
                    marginBottom: '40px',
                }}>
                    <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem', fontWeight: 700 }}>Ready to receive UBI?</h2>
                    <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px', fontSize: '0.95rem' }}>
                        Join the community and start receiving distributions from hives that match your profile.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Link to="/register" style={{
                            background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                            color: '#000',
                            padding: '12px 28px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            textDecoration: 'none',
                            boxShadow: '0 4px 20px rgba(255,193,7,0.3)',
                        }}>
                            Create Account
                        </Link>
                        <Link to="/login" style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: '#fff',
                            padding: '12px 28px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            textDecoration: 'none',
                        }}>
                            Log In
                        </Link>
                    </div>
                </div>}

                {/* Footer */}
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '24px' }}>
                    <div style={{ marginBottom: '4px' }}>© {new Date().getFullYear()} You-Bee. All rights reserved.</div>
                    <div style={{ marginBottom: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>Patent Pending</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
                        <Link to="/" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Home</Link>
                        <Link to="/privacy" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Privacy Policy</Link>
                        <Link to="/terms" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Terms of Service</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Stats;
